// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Aircraft photo lifecycle: rendering presentation (image + fallback
// label), syncing the photo trigger / clear controls, reading a file
// into a data URL, and downscaling oversized images to a max dimension
// before persistence.
//
// Exposed as a factory because setAircraftFormPhotoDataUrl mutates the
// aircraftFormPhotoDataUrl module-level binding in app.js.

export function createAircraftPhoto({
  aircraftPhotoFallbackLabel,
  appReadOnlyMode,
  selectedAircraftWorkspace,
  getAircraftFormPhotoDataUrl,
  setAircraftFormPhotoDataUrlValue,
  requestAircraftWorkspaceRender = () => {},
}) {
  // The /api/aircraft/:id/photo endpoint is JWT-gated, but an <img src> load is
  // an image subresource request that can't carry the Authorization header (only
  // the patched window.fetch injects it). Pointing <img> straight at the endpoint
  // 401s/404s on a page refresh and the photo breaks. So, exactly like the flight
  // report PDF (src/app/flight-report-pdf.js), fetch the bytes through the
  // auth-aware fetch, cache them as a same-origin blob: URL, and feed that to the
  // <img>. The endpoint URL carries a ?v= token that changes whenever the photo
  // changes, so each cache key maps to one immutable image.
  const photoBlobCache = new Map();   // endpoint URL -> blob: object URL
  const photoBlobInFlight = new Set(); // endpoint URLs currently being fetched
  const photoBlobFailed = new Set();   // endpoint URLs that failed (don't retry this session)
  let photoRerenderQueued = false;

  function isAircraftPhotoEndpoint(value) {
    return /\/api\/aircraft\/[^/]+\/photo(?:[?#]|$)/i.test(value);
  }

  function queueAircraftPhotoRerender() {
    // Coalesce multiple blob completions in the same tick into one re-render.
    if (photoRerenderQueued) return;
    photoRerenderQueued = true;
    const flush = () => {
      photoRerenderQueued = false;
      requestAircraftWorkspaceRender();
    };
    if (typeof queueMicrotask === "function") queueMicrotask(flush);
    else Promise.resolve().then(flush);
  }

  function loadAircraftPhotoBlob(url) {
    if (photoBlobCache.has(url) || photoBlobInFlight.has(url) || photoBlobFailed.has(url)) return;
    photoBlobInFlight.add(url);
    (async () => {
      try {
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) { photoBlobFailed.add(url); return; }
        const blob = await response.blob();
        if (!blob.size) { photoBlobFailed.add(url); return; }
        photoBlobCache.set(url, URL.createObjectURL(blob));
        queueAircraftPhotoRerender();
      } catch (error) {
        photoBlobFailed.add(url);
        console.warn("Aircraft photo fetch failed", error);
      } finally {
        photoBlobInFlight.delete(url);
      }
    })();
  }

  // Resolve a profile photo value into an <img src> that can load right now:
  // data:/blob: URLs pass through, the photo endpoint resolves to its cached
  // blob: URL (kicking off the fetch on a miss and returning null until it
  // lands), and anything else passes through unchanged.
  function aircraftPhotoDisplaySrc(value) {
    const src = typeof value === "string" ? value.trim() : "";
    if (!src) return null;
    if (/^(?:data:|blob:)/i.test(src)) return src;
    if (!isAircraftPhotoEndpoint(src)) return src;
    const cached = photoBlobCache.get(src);
    if (cached) return cached;
    loadAircraftPhotoBlob(src);
    return null;
  }

  function setAircraftPhotoPresentation(imageElement, fallbackElement, dataUrl, label) {
    const displaySrc = aircraftPhotoDisplaySrc(dataUrl);
    if (imageElement) {
      if (displaySrc) {
        imageElement.src = displaySrc;
        imageElement.alt = label ? `${label} aircraft photo` : "Aircraft photo";
        imageElement.hidden = false;
      } else {
        imageElement.removeAttribute("src");
        imageElement.alt = "";
        imageElement.hidden = true;
      }
    }
    if (fallbackElement) {
      fallbackElement.textContent = fallbackElement.id === "aircraftWorkspaceImageFallback"
        ? ""
        : aircraftPhotoFallbackLabel(label);
      fallbackElement.hidden = Boolean(displaySrc);
    }
  }

  function updateAircraftPhotoControls(label, dataUrl) {
    const trigger = document.querySelector("#aircraftWorkspacePhotoTrigger");
    const action = document.querySelector("#aircraftWorkspacePhotoAction");
    const clearButton = document.querySelector("#aircraftPhotoClear");
    const readOnly = appReadOnlyMode();
    if (trigger) trigger.classList.toggle("is-empty", !dataUrl);
    if (action) action.textContent = dataUrl ? "Change photo" : "Add photo";
    if (trigger) {
      trigger.classList.toggle("is-readonly", readOnly);
      trigger.hidden = false;
      if (readOnly) {
        trigger.dataset.readonlyOriginalFor = trigger.dataset.readonlyOriginalFor ?? trigger.getAttribute("for") ?? "";
        trigger.removeAttribute("for");
        trigger.removeAttribute("tabindex");
      } else if (trigger.dataset.readonlyOriginalFor !== undefined) {
        const originalFor = trigger.dataset.readonlyOriginalFor;
        if (originalFor) trigger.setAttribute("for", originalFor);
        delete trigger.dataset.readonlyOriginalFor;
      }
    }
    if (action) {
      action.hidden = readOnly;
    }
    if (clearButton) {
      clearButton.disabled = readOnly || !dataUrl;
      clearButton.hidden = readOnly || !dataUrl;
    }
  }

  function setAircraftFormPhotoDataUrl(dataUrl, label = selectedAircraftWorkspace()) {
    setAircraftFormPhotoDataUrlValue(dataUrl || null);
    updateAircraftPhotoControls(label, getAircraftFormPhotoDataUrl());
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(new Error(`Could not read ${file?.name ?? "image file"}.`));
      reader.readAsDataURL(file);
    });
  }

  function loadImageElement(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not process the selected image."));
      image.src = src;
    });
  }

  async function normalizedAircraftPhotoDataUrl(file) {
    if (!file) return null;
    if (!String(file.type ?? "").startsWith("image/")) {
      throw new Error("Choose an image file for the aircraft photo.");
    }
    if (file.size > 8 * 1024 * 1024) {
      throw new Error("Choose an image smaller than 8 MB.");
    }
    const sourceDataUrl = await readFileAsDataUrl(file);
    const image = await loadImageElement(sourceDataUrl);
    const maxDimension = 1024;
    const largestDimension = Math.max(image.naturalWidth || image.width || 0, image.naturalHeight || image.height || 0);
    if (!largestDimension) {
      throw new Error("The selected image could not be decoded.");
    }
    if (largestDimension <= maxDimension && sourceDataUrl.length <= 1_600_000) {
      return sourceDataUrl;
    }
    const scale = Math.min(1, maxDimension / largestDimension);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    canvas.height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("This browser could not prepare the aircraft image.");
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.86);
  }

  return {
    setAircraftPhotoPresentation,
    updateAircraftPhotoControls,
    setAircraftFormPhotoDataUrl,
    aircraftPhotoDisplaySrc,
    readFileAsDataUrl,
    loadImageElement,
    normalizedAircraftPhotoDataUrl,
  };
}
